import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { UrgentChip } from "@/components/client/UrgentMarker";
import { PageHeader, FilterBar, SummaryTile } from "@/components/shared/PageChrome";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { AsyncPaginatedSelect, directoryFetcher } from "@/components/shared/AsyncPaginatedSelect";
import { ViewTabs } from "@/components/shared/ListChrome";
import { Checkbox } from "@/components/ui/checkbox";
import { PipelineStrip } from "@/components/enquiries/PipelineStrip";
import { BulkAssignConfirm } from "@/components/enquiries/BulkAssignConfirm";
import { IntakeFields, EMPTY_INTAKE, toIntakePayload } from "@/components/enquiries/IntakeFields";
import { BoroughSelect, useBoroughOptions } from "@/components/shared/BoroughSelect";

// Saved operational views: each is a named preset over the real list filters (status / assigned) - nothing beyond the API's own semantics.
const VIEWS = [
  { value: "ALL", label: "All enquiries", filters: {} },
  { value: "UNASSIGNED", label: "Unassigned", filters: { assigned: "UNASSIGNED" } },
  { value: "MINE", label: "Assigned to me", filters: { assigned: "ME" } },
  { value: "SOURCING", label: "Sourcing", filters: { status: "SOURCING" } },
  { value: "CLIENT_DECISION", label: "Awaiting client decision", filters: { status: "CLIENT_DECISION" } },
  { value: "READY_TO_BOOK", label: "Ready to book", filters: { status: "READY_TO_BOOK" } },
];
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const STATUSES = ["REQUEST", "ASSIGNED", "SOURCING", "OFFERS", "CLIENT_DECISION", "READY_TO_BOOK", "COMPLETED", "CANCELLED"];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

const EMPTY_FORM = {
  client_organisation_id: "", booker_contact_id: "", guest_lead_name: "",
  location_text: "", postcode: "", arrival_date: "", departure_date: "",
  rooms_required: 1, guests_count: 1, priority: "MEDIUM", urgency_notes: "", brief_received_at: "", brief_type: "",
};

function NewEnquiryDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [contacts, setContacts] = useState([]);
  const [intake, setIntake] = useState({ ...EMPTY_INTAKE });
  const [showIntake, setShowIntake] = useState(false);
  const [intakeOptions, setIntakeOptions] = useState(null);
  useEffect(() => { if (open && !intakeOptions) api.get("/enquiries/intake-options").then((r) => setIntakeOptions(r.data)).catch(() => {}); }, [open, intakeOptions]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setContacts([]);
    setFormError("");
  }, [open]);

  useEffect(() => {
    if (!form.client_organisation_id) { setContacts([]); return; }
    api.get(`/directory/clients/${form.client_organisation_id}`).then((r) => setContacts(r.data.contacts || [])).catch(() => setContacts([]));
  }, [form.client_organisation_id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setFormError("");
    if (!form.client_organisation_id) return setFormError("Select a client organisation.");
    if (!form.location_text.trim()) return setFormError("Destination / location is required.");
    if (!form.arrival_date || !form.departure_date) return setFormError("Arrival and departure dates are required.");
    if (form.departure_date <= form.arrival_date) return setFormError("Departure date must be after the arrival date.");

    setSubmitting(true);
    try {
      const payload = {
        client_organisation_id: form.client_organisation_id,
        booker_contact_id: form.booker_contact_id || null,
        guest_lead_name: form.guest_lead_name.trim() || null,
        location_text: form.location_text.trim(),
        postcode: form.postcode.trim() || null,
        arrival_date: form.arrival_date,
        departure_date: form.departure_date,
        rooms_required: Number(form.rooms_required) || 1,
        guests_count: Number(form.guests_count) || 1,
        priority: form.priority,
        urgency_notes: form.urgency_notes.trim() || null,
        brief_received_at: form.brief_received_at ? new Date(form.brief_received_at).toISOString() : null,
        brief_type: form.brief_type || null,
        intake: showIntake ? toIntakePayload(intake) : null,
      };
      const r = await api.post("/enquiries", payload);
      toast.success(`Enquiry ${r.data.jigsaw_reference} created`);
      onCreated(r.data.id);
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Could not create this enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-enquiry-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New enquiry</DialogTitle>
          <DialogDescription>Staff-only intake. Every case starts in REQUEST for triage.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="ne-client">Client organisation *</Label>
            <AsyncPaginatedSelect testId="new-enquiry-client-select" value={form.client_organisation_id} placeholder="Select an active client"
              fetchPage={directoryFetcher("/directory/clients", { active_only: true })} getSub={(c) => c.tier ? `Tier ${c.tier}` : null}
              onChange={(id) => setForm((f) => ({ ...f, client_organisation_id: id, booker_contact_id: "" }))} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ne-contact">Client reference contact</Label>
            <Select value={form.booker_contact_id} onValueChange={set("booker_contact_id")} disabled={!form.client_organisation_id}>
              <SelectTrigger id="ne-contact" data-testid="new-enquiry-contact-select"><SelectValue placeholder={form.client_organisation_id ? "Optional booker contact" : "Select a client first"} /></SelectTrigger>
              <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}{c.job_title ? ` — ${c.job_title}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ne-lead">Lead guest name (this stay)</Label>
            <Input id="ne-lead" data-testid="new-enquiry-guest-lead-input" value={form.guest_lead_name} onChange={(e) => set("guest_lead_name")(e.target.value)} placeholder="Optional — the guest, not the booker" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ne-location">Destination / location *</Label>
            <Input id="ne-location" data-testid="new-enquiry-location-input" value={form.location_text} onChange={(e) => set("location_text")(e.target.value)} placeholder="e.g. Manchester city centre" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ne-postcode">Postcode</Label>
            <Input id="ne-postcode" data-testid="new-enquiry-postcode-input" value={form.postcode} onChange={(e) => set("postcode")(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ne-arrival">Arrival date *</Label>
            <Input id="ne-arrival" type="date" data-testid="new-enquiry-arrival-input" value={form.arrival_date} onChange={(e) => set("arrival_date")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ne-departure">Departure date *</Label>
            <Input id="ne-departure" type="date" data-testid="new-enquiry-departure-input" value={form.departure_date} onChange={(e) => set("departure_date")(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ne-rooms">Rooms required</Label>
            <Input id="ne-rooms" type="number" min="1" data-testid="new-enquiry-rooms-input" value={form.rooms_required} onChange={(e) => set("rooms_required")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ne-guests">Guest count</Label>
            <Input id="ne-guests" type="number" min="1" data-testid="new-enquiry-guests-input" value={form.guests_count} onChange={(e) => set("guests_count")(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ne-priority">Priority</Label>
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger id="ne-priority" data-testid="new-enquiry-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="ne-notes">Accommodation needs / notes</Label>
            <Textarea id="ne-notes" data-testid="new-enquiry-notes-textarea" rows={3} value={form.urgency_notes} onChange={(e) => set("urgency_notes")(e.target.value)} placeholder="Accessibility, dietary, urgency context, etc." />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 rounded-md border border-dashed border-jigsaw-border p-3">
          <p className="sm:col-span-2 text-[12px] text-jigsaw-muted">Brief evidence (optional, staff-recorded fact - leave blank if not known; nothing is assumed)</p>
          <div className="space-y-1">
            <Label htmlFor="ne-brief-received">Brief received (actual time)</Label>
            <Input id="ne-brief-received" data-testid="new-enquiry-brief-received" type="datetime-local" max={new Date().toISOString().slice(0, 16)} value={form.brief_received_at} onChange={(e) => set("brief_received_at")(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ne-brief-type">Brief type</Label>
            <Select value={form.brief_type || "UNSET"} onValueChange={(v) => set("brief_type")(v === "UNSET" ? "" : v)}>
              <SelectTrigger id="ne-brief-type" data-testid="new-enquiry-brief-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="UNSET">Not recorded</SelectItem><SelectItem value="ACCOMMODATION">Accommodation</SelectItem><SelectItem value="VENUE_FINDING">Venue-finding</SelectItem><SelectItem value="MIXED">Mixed</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        {formError && <p data-testid="new-enquiry-error" className="text-sm text-jigsaw-danger">{formError}</p>}

        <div className="mt-4 border-t border-jigsaw-border pt-3">
          <button type="button" data-testid="new-enquiry-toggle-intake" className="text-[13px] font-medium text-jigsaw-navy underline" onClick={() => setShowIntake((v) => !v)}>{showIntake ? "Hide intake details" : "Add intake details (staff only)"}</button>
          {showIntake && intakeOptions && <div className="mt-3"><IntakeFields value={intake} onChange={setIntake} options={intakeOptions} contacts={contacts} /></div>}
        </div>
        <DialogFooter>
          <Button data-testid="new-enquiry-submit-button" onClick={submit} disabled={submitting}>
            {submitting ? "Creating..." : "Create enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EnquiriesList() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState(null);
  const [error, setError] = useState(false);
  const [newEnquiryOpen, setNewEnquiryOpen] = useState(false);
  const navigate = useNavigate();

  const status = params.get("status") || "ALL";
  const q = params.get("q") || "";
  const assigned = params.get("assigned") || "";
  const borough = params.get("borough") || "";
  const boroughOptions = useBoroughOptions();
  const view = VIEWS.find((v) => (v.filters.assigned || "") === assigned && (v.filters.status || "ALL") === status)?.value || "CUSTOM";
  const [selected, setSelected] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [bulkStaff, setBulkStaff] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [viewCounts, setViewCounts] = useState({});
  const [pipeline, setPipeline] = useState(null);

  const PAGE_SIZE = 25;
  const rawPage = params.get("page");
  const page = Math.max(1, parseInt(rawPage || "1", 10) || 1);

  // Real counts per saved view + whole-set pipeline (unfiltered metrics), refreshed after any bulk change.
  const loadViewCounts = () => Promise.all(VIEWS.map((v) => api.get("/enquiries/metrics", { params: v.filters }).then((r) => [v, r.data]).catch(() => [v, null])))
    .then((pairs) => {
      setViewCounts(Object.fromEntries(pairs.filter(([, m]) => m).map(([v, m]) => [v.value, v.value === "ALL" ? m.total : v.filters.status ? m.total : m.open])));
      const all = pairs.find(([v]) => v.value === "ALL")?.[1];
      setPipeline(all ? all.by_status : null);
    });

  useEffect(() => { api.get("/people/staff-users").then((r) => setStaffOptions(r.data.items)).catch(() => setStaffOptions([])); loadViewCounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelected([]);
    const query = { page, page_size: PAGE_SIZE };
    if (status !== "ALL") query.status = status;
    if (q) query.q = q;
    if (assigned) query.assigned = assigned;
    if (borough) query.borough = borough;
    const filters = {}; if (status !== "ALL") filters.status = status; if (q) filters.q = q; if (assigned) filters.assigned = assigned; if (borough) filters.borough = borough;
    api.get("/enquiries/metrics", { params: filters }).then((r) => setMetrics(r.data)).catch(() => setMetrics(null));
    api.get("/enquiries", { params: query }).then((r) => {
      const d = r.data;
      setData(d);
      if (d.page !== page || (rawPage !== null && String(d.page) !== rawPage)) { // server clamped an invalid/out-of-range page: mirror the real page in the URL
        const next = new URLSearchParams(params); if (d.page <= 1) next.delete("page"); else next.set("page", String(d.page)); setParams(next, { replace: true });
      }
    }).catch(() => setError(true));
  }, [status, q, page, assigned, borough]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (mutate) => { const next = new URLSearchParams(params); mutate(next); setParams(next); };
  const setStatus = (v) => update((n) => { if (v === "ALL") n.delete("status"); else n.set("status", v); n.delete("page"); }); // filters reset paging
  const setBorough = (v) => update((n) => { if (!v) n.delete("borough"); else n.set("borough", v); n.delete("page"); });
  const setQ = (v) => update((n) => { if (!v) n.delete("q"); else n.set("q", v); n.delete("page"); });
  const setPage = (p) => update((n) => { if (p <= 1) n.delete("page"); else n.set("page", String(p)); });
  const setView = (v) => { const f = VIEWS.find((x) => x.value === v)?.filters || {}; update((n) => { n.delete("status"); n.delete("assigned"); n.delete("page"); if (f.status) n.set("status", f.status); if (f.assigned) n.set("assigned", f.assigned); }); };

  const openRows = data ? data.items.filter((e) => !["COMPLETED", "CANCELLED"].includes(e.status)) : [];
  const toggleRow = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(selected.length === openRows.length ? [] : openRows.map((e) => e.id));
  const bulkAssign = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/enquiries/bulk-assign", { enquiry_ids: selected, staff_user_id: bulkStaff });
      setBulkResult(r.data); setSelected([]); setBulkStaff(""); setConfirmOpen(false);
      toast[r.data.skipped.length ? "warning" : "success"](`${r.data.assigned.length} assigned to ${r.data.staff_name}${r.data.skipped.length ? ` · ${r.data.skipped.length} skipped` : ""}`);
      const query = { page, page_size: PAGE_SIZE }; if (status !== "ALL") query.status = status; if (q) query.q = q; if (assigned) query.assigned = assigned; if (borough) query.borough = borough;
      api.get("/enquiries", { params: query }).then((res) => setData(res.data));
      loadViewCounts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk assign failed"); } finally { setBulkBusy(false); }
  };
  const selectedRows = data ? data.items.filter((e) => selected.includes(e.id)) : [];
  const bulkStaffName = staffOptions.find((u) => u.id === bulkStaff)?.full_name || "";
  const filtersActive = status !== "ALL" || !!q || !!assigned;
  const [searchText, setSearchText] = useState(q);
  const resetFilters = () => { setSearchText(""); update((n) => { n.delete("status"); n.delete("assigned"); n.delete("q"); n.delete("page"); }); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filters = {}; if (status !== "ALL") filters.status = status; if (q) filters.q = q; if (assigned) filters.assigned = assigned; if (borough) filters.borough = borough;
      const r = await api.get("/enquiries/export.csv", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "enquiries.csv"; a.click(); URL.revokeObjectURL(url);
      const rows = r.headers["x-export-rows"], total = r.headers["x-export-total"], truncated = r.headers["x-export-truncated"] === "true";
      setExportNote(truncated ? { tone: "warning", text: `Export limited to the first ${rows} of ${total} matching enquiries - narrow the filters to export the rest.` } : { tone: "ok", text: `Exported ${rows} of ${total} matching enquiries.` });
      toast[truncated ? "warning" : "success"](truncated ? `Exported the first ${rows} of ${total} - export limit reached` : `Exported ${rows} enquiries`);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  useEffect(() => {
    if (params.get("new") === "1") {
      setNewEnquiryOpen(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="enquiries-list-page" className="space-y-5">
      <PageHeader title="Enquiries" subtitle="Triage, assign and progress incoming accommodation requests."
        chip={data && <span data-testid="enquiries-total-chip" className="rounded-full border border-jigsaw-border px-2.5 py-0.5 text-[12px] text-jigsaw-muted">{data.total} {status === "ALL" ? "total" : "matching"}</span>}
        actions={<><Button data-testid="export-enquiries-button" variant="outline" disabled={exporting || !data || data.total === 0} onClick={exportCsv}><Download className="mr-1.5 h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}</Button>
          <Button data-testid="new-enquiry-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" onClick={() => setNewEnquiryOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> New enquiry</Button></>} />

      {exportNote && (
        <p role="status" data-testid="export-note" className={`rounded-md border px-3 py-2 text-[13px] ${exportNote.tone === "warning" ? "border-jigsaw-warning/40 bg-jigsaw-warningBg text-jigsaw-warning" : "border-jigsaw-border bg-white text-jigsaw-muted"}`}>{exportNote.text}</p>
      )}
      {metrics && (
        <div data-testid="enquiries-metrics" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SummaryTile testId="metric-total" label={status === "ALL" && !q ? "All enquiries" : "Matching filters"} value={metrics.total} sub="Full filtered set, not this page" />
          <SummaryTile testId="metric-open" label="Open" value={metrics.open} sub="Not completed or cancelled" />
          <SummaryTile testId="metric-unassigned" label="Unassigned (open)" value={metrics.unassigned_open} sub="Need an owner" tone={metrics.unassigned_open ? "warning" : "default"} />
          <SummaryTile testId="metric-high" label="High / critical (open)" value={metrics.high_priority_open} sub="Priority attention" tone={metrics.high_priority_open ? "danger" : "default"} />
        </div>
      )}

      <NewEnquiryDialog
        open={newEnquiryOpen}
        onOpenChange={setNewEnquiryOpen}
        onCreated={(id) => { setNewEnquiryOpen(false); navigate(`/control-centre/enquiries/${id}`); }}
      />

      <ViewTabs testId="enquiries-views" tabs={view === "CUSTOM" ? [...VIEWS, { value: "CUSTOM", label: "Custom filter" }] : VIEWS} value={view} onChange={setView} counts={viewCounts} />

      <PipelineStrip byStatus={pipeline} active={status} onSelect={setStatus} />

      <BulkAssignConfirm open={confirmOpen} onOpenChange={setConfirmOpen} rows={selectedRows} staffName={bulkStaffName} onConfirm={bulkAssign} busy={bulkBusy} />

      {selected.length > 0 && (
        <div data-testid="enquiries-bulk-bar" className="flex flex-col gap-2 rounded-[10px] border border-jigsaw-navy/15 bg-[#EEF3FB] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] font-medium text-jigsaw-navy">{selected.length} open enquir{selected.length === 1 ? "y" : "ies"} selected</p>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={bulkStaff} onValueChange={setBulkStaff}>
              <SelectTrigger data-testid="bulk-assign-staff-select" className="h-9 w-56"><SelectValue placeholder="Assign to staff member…" /></SelectTrigger>
              <SelectContent>{staffOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="bulk-assign-button" size="sm" disabled={!bulkStaff} onClick={() => setConfirmOpen(true)}>Assign selected…</Button>
            <Button data-testid="bulk-clear-button" size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
          </div>
        </div>
      )}
      {bulkResult && bulkResult.skipped.length > 0 && (
        <p role="status" data-testid="bulk-skipped-note" className="rounded-md border border-jigsaw-warning/40 bg-jigsaw-warningBg px-3 py-2 text-[13px] text-jigsaw-warning">Skipped: {bulkResult.skipped.map((s) => `${s.reference || s.id} (${s.reason})`).join(", ")}</p>
      )}

      <FilterBar testId="enquiries-filter-bar" summary={data ? `${data.total} ${status === "ALL" ? "" : status.replace(/_/g, " ").toLowerCase() + " "}enquiries match` : null}>
        <Input data-testid="enquiries-search-input" placeholder="Filter by reference or location..." value={searchText} onChange={(e) => { setSearchText(e.target.value); setQ(e.target.value); }} className="h-9 w-full sm:max-w-xs" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="enquiries-status-filter" className="h-9 w-full sm:w-52"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <BoroughSelect id="enquiries-borough-filter" label={null} value={borough} onChange={setBorough} options={boroughOptions} allLabel="All boroughs" allowNotRecordedFilter className="h-9 w-full sm:w-56" />
        {filtersActive && <Button data-testid="enquiries-reset-filters" variant="ghost" size="sm" className="h-9 text-jigsaw-orange" onClick={resetFilters}>Reset filters</Button>}
      </FilterBar>

      {error && <ErrorState testId="enquiries-error" />}
      {!data && !error && <LoadingState testId="enquiries-loading" />}
      {data && data.items.length === 0 && (
        <EmptyState testId="enquiries-empty" title="No enquiries match these filters" description="Try clearing the status filter or search term." />
      )}

      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <StackedList testId="enquiries-stacked" items={data.items} title={(e) => `${e.jigsaw_reference} · ${e.client_name}`} status={(e) => <StatusBadge status={e.status} />} openLabel="Open enquiry"
            onOpen={(e) => navigate(`/control-centre/enquiries/${e.id}`)}
            fields={[{ label: "Location", value: (e) => e.location_text, full: true }, { label: "Dates", value: (e) => `${e.arrival_date} → ${e.departure_date}`, mono: true, full: true },
              { label: "Rooms / guests", value: (e) => `${e.rooms_required} / ${e.guests_count}` }, { label: "Priority", value: (e) => e.priority }, { label: "Response target", value: (e) => (e.sla?.state === "NOT_CONFIGURED" ? "Not configured" : e.sla?.state?.replace(/_/g, " ").toLowerCase()) }, { label: "Assigned", value: (e) => e.assigned_staff_name || "Unassigned", full: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="enquiries-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox data-testid="enquiries-select-all" aria-label="Select all open enquiries on this page" checked={openRows.length > 0 && selected.length === openRows.length} disabled={openRows.length === 0} onCheckedChange={toggleAll} /></TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Rooms / Guests</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Response target</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((e) => (
                <TableRow
                  key={e.id}
                  data-testid={`enquiry-row-${e.jigsaw_reference}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/control-centre/enquiries/${e.id}`)}
                >
                  <TableCell onClick={(ev) => ev.stopPropagation()}>{!["COMPLETED", "CANCELLED"].includes(e.status) && <Checkbox data-testid={`enquiry-select-${e.jigsaw_reference}`} aria-label={`Select ${e.jigsaw_reference}`} checked={selected.includes(e.id)} onCheckedChange={() => toggleRow(e.id)} />}</TableCell>
                  <TableCell className="font-mono-ref font-semibold text-jigsaw-navy"><span className="flex items-center gap-2">{e.jigsaw_reference}<UrgentChip urgent={e.client_urgent} testId={`enquiry-urgent-${e.jigsaw_reference}`} /></span></TableCell>
                  <TableCell className="font-medium">{e.client_name}</TableCell>
                  <TableCell>{e.location_text || "—"}</TableCell>
                  <TableCell className="font-mono-ref whitespace-nowrap text-[13px]">{e.arrival_date} &rarr; {e.departure_date}</TableCell>
                  <TableCell className="whitespace-nowrap">{e.rooms_required} rooms <span className="text-jigsaw-muted">/ {e.guests_count} guests</span></TableCell>
                  <TableCell>{e.assigned_staff_name || <span className="rounded-md border border-dashed border-jigsaw-border px-2 py-0.5 text-[12px] text-jigsaw-muted">Unassigned</span>}</TableCell>
                  <TableCell><StatusBadge status={e.priority} /></TableCell>
                  <TableCell data-testid={`enquiry-sla-${e.jigsaw_reference}`}>{e.sla?.state === "NOT_CONFIGURED" ? <span className="text-[12px] text-jigsaw-muted" title="Response targets are not configured - set them under Admin › Response targets">Not configured</span> : <span title={e.sla?.first_response?.due_at ? `First response due ${new Date(e.sla.first_response.due_at).toLocaleString("en-GB")}` : undefined}><StatusBadge status={e.sla?.state} /></span>}</TableCell>
                  <TableCell><StatusBadge status={e.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="enquiries-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
